const SALON_DATA_CACHE = new Map();
const SALON_CENTROID_CACHE = new Map();
const SALON_PLACE_CACHE = new Map();
const SALON_ASSET_CACHE = new Map();

const DESKTOP_QUERY = '(min-width: 1024px)';
const SEARCH_DEBOUNCE_MS = 250;
const PIN_LENGTH = 6;
const PIN_PREFIX_LENGTH = 3;
const DEFAULT_RADIUS_KM = 25;
const DEFAULT_MIN_RESULTS = 5;
const SECTION_ROOT_MARGIN = '400px';
const LIST_ROOT_MARGIN = '200px';
const DISTANCE_ZOOM = 11;
const EARTH_RADIUS_KM = 6371;
const GEO_OPTIONS = {
  enableHighAccuracy: false,
  timeout: 8000,
  maximumAge: 300000,
};
const TOGGLE_LABEL_MAP = 'MAP';
const TOGGLE_LABEL_LIST = 'LIST';

const matchKey = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const MATCH_MISS = 0;
const MATCH_EXACT = 1;
const MATCH_PREFIX = 2;

const matchField = (value, queryWords, queryKey) => {
  const normalised = matchKey(value);
  if (normalised === '') return MATCH_MISS;
  if (normalised === queryKey) return MATCH_EXACT;
  const tokens = normalised.split(' ');
  const everyWordStartsAToken = queryWords.every((word) => tokens.some((token) => token.startsWith(word)));
  return everyWordStartsAToken ? MATCH_PREFIX : MATCH_MISS;
};

const matchTier = (salon, queryWords, queryKey) => {
  if (
    matchField(salon.pincode, queryWords, queryKey) === MATCH_EXACT ||
    matchField(salon.city, queryWords, queryKey) === MATCH_EXACT
  ) {
    return 0;
  }
  if (matchField(salon.city, queryWords, queryKey) !== MATCH_MISS) return 1;
  if (matchField(salon.pincode, queryWords, queryKey) !== MATCH_MISS) return 2;
  if (matchField(salon.area, queryWords, queryKey) !== MATCH_MISS) return 3;
  if (matchField(salon.name, queryWords, queryKey) !== MATCH_MISS) return 4;
  if (matchField(salon.search, queryWords, queryKey) !== MATCH_MISS) return 5;
  return -1;
};

const haversineKm = (fromLat, fromLng, toLat, toLng) => {
  const toRadians = (degrees) => (degrees * Math.PI) / 180;
  const deltaLat = toRadians(toLat - fromLat);
  const deltaLng = toRadians(toLng - fromLng);
  const a =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(toRadians(fromLat)) * Math.cos(toRadians(toLat)) * Math.sin(deltaLng / 2) ** 2;
  return EARTH_RADIUS_KM * 2 * Math.asin(Math.min(1, Math.sqrt(a)));
};

const formatKm = (km) => `${km < 10 ? km.toFixed(1) : Math.round(km)} km`;

// The salon data lists no treatments for this salon (unknown, not "none").
const isUnconfirmed = (salon) => (salon.treatments ?? []).length === 0;

const debounce = (callback, wait) => {
  let timer = null;
  const debounced = (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => callback(...args), wait);
  };
  debounced.cancel = () => clearTimeout(timer);
  return debounced;
};

const fetchSalons = (url) => {
  if (!SALON_DATA_CACHE.has(url)) {
    SALON_DATA_CACHE.set(
      url,
      fetch(url).then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        const salons = payload?.salons;
        if (!Array.isArray(salons) || salons.length === 0) throw new Error('No salons array');
        const usable = salons.filter((salon) => typeof salon?.name === 'string' && salon.name !== '');
        if (usable.length === 0) throw new Error('No named salon records');
        return usable;
      }),
    );
  }
  return SALON_DATA_CACHE.get(url);
};

const injectAsset = (url, build) => {
  if (!SALON_ASSET_CACHE.has(url)) {
    SALON_ASSET_CACHE.set(
      url,
      new Promise((resolve, reject) => {
        const element = build();
        element.addEventListener('load', () => resolve(url), { once: true });
        element.addEventListener('error', () => reject(new Error(`Could not load ${url}`)), { once: true });
        document.head.appendChild(element);
      }),
    );
  }
  return SALON_ASSET_CACHE.get(url);
};

const loadStylesheet = (url) =>
  injectAsset(url, () => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    return link;
  });

const loadScript = (url) =>
  injectAsset(url, () => {
    const script = document.createElement('script');
    script.src = url;
    script.async = false;
    return script;
  });

const ensureMapAssets = async (assets) => {
  if (Object.values(assets).some((url) => url === '')) {
    throw new Error('Missing map asset URL');
  }
  await Promise.all([loadStylesheet(assets.leafletCss), loadStylesheet(assets.clusterCss)]);
  await loadScript(assets.leafletJs);
  await Promise.all([loadScript(assets.clusterJs), loadScript(assets.mapJs)]);
  if (!window.SalonMapView) throw new Error('SalonMapView never registered');
};

const fetchPlaces = (url) => {
  if (!SALON_PLACE_CACHE.has(url)) {
    SALON_PLACE_CACHE.set(
      url,
      fetch(url).then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        return payload?.places ?? {};
      }),
    );
  }
  return SALON_PLACE_CACHE.get(url);
};

const fetchCentroids = (url) => {
  if (!SALON_CENTROID_CACHE.has(url)) {
    SALON_CENTROID_CACHE.set(
      url,
      fetch(url).then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        return payload?.centroids ?? {};
      }),
    );
  }
  return SALON_CENTROID_CACHE.get(url);
};

class SalonLocator extends HTMLElement {
  connectedCallback() {
    this.readConfig();
    this.cacheElements();

    this.state = {
      all: [],
      filtered: [],
      visible: [],
      visibleRule: 'all',
      distances: new Map(),
      rendered: 0,
      query: '',
      chip: '',
      origin: null,
      originSource: null,
      unmatched: false,
      sortMode: 'city',
      activeId: null,
      focusId: null,
      focusName: null,
      view: this.dataset.locatorView ?? 'list',
      ready: false,
      mapRequested: false,
    };

    this.filterToken = 0;
    this.isLoading = false;
    this.pinnedByGeolocation = false;
    this.geoOrigin = null;
    this.mapView = null;
    this.mapPromise = null;
    this.bindListeners();
    this.observeSection();
  }

  disconnectedCallback() {
    this.sectionObserver?.disconnect();
    this.listObserver?.disconnect();
    this.debouncedSearch?.cancel();
    this.unbindListeners();
    this.mapView?.destroy();
    this.mapView = null;
    this.mapPromise = null;
  }

  readConfig() {
    const { dataset } = this;
    this.config = {
      salonUrl: dataset.locatorSalonUrl ?? '',
      pinUrl: dataset.locatorPinUrl ?? '',
      placeUrl: dataset.locatorPlaceUrl ?? '',
      lat: Number.parseFloat(dataset.locatorLat ?? ''),
      lng: Number.parseFloat(dataset.locatorLng ?? ''),
      zoom: Number.parseInt(dataset.locatorZoom ?? '', 10) || 5,
      batch: Number.parseInt(dataset.locatorBatch ?? '', 10) || 20,
      radiusKm: Number.parseFloat(dataset.locatorRadius ?? '') || DEFAULT_RADIUS_KM,
      minResults: Number.parseInt(dataset.locatorMinResults ?? '', 10) || DEFAULT_MIN_RESULTS,
      aliases: SalonLocator.parseAliases(dataset.locatorAliases ?? ''),
      tileUrl: dataset.locatorTileUrl ?? '',
      tileAttr: dataset.locatorTileAttr ?? '',
      assets: {
        leafletCss: dataset.locatorLeafletCss ?? '',
        clusterCss: dataset.locatorClusterCss ?? '',
        leafletJs: dataset.locatorLeafletJs ?? '',
        clusterJs: dataset.locatorClusterJs ?? '',
        mapJs: dataset.locatorMapJs ?? '',
      },
    };
  }

  static parseAliases(raw) {
    const aliases = new Map();
    for (const line of raw.split('\n')) {
      const separator = line.indexOf('=');
      if (separator < 1) continue;
      const variant = matchKey(line.slice(0, separator));
      const canonical = matchKey(line.slice(separator + 1));
      if (variant !== '' && canonical !== '') aliases.set(variant, canonical);
    }
    return aliases;
  }

  cacheElements() {
    this.wrapper = this.closest('.salon-near-me');
    this.form = this.querySelector('[data-locator-form]');
    this.input = this.querySelector('[data-locator-input]');
    this.validation = this.querySelector('[data-locator-validation]');
    this.chipRow = this.querySelector('[data-locator-chips]');
    this.panel = this.querySelector('[data-locator-panel]');
    this.banner = this.querySelector('[data-locator-banner]');
    this.bannerButton = this.querySelector('[data-locator-banner-btn]');
    this.message = this.querySelector('[data-locator-message]');
    this.messageText = this.querySelector('[data-locator-message-text]');
    this.messageNote = this.querySelector('[data-locator-message-note]');
    this.messageReset = this.querySelector('[data-locator-message-reset]');
    this.list = this.querySelector('[data-locator-list]');
    this.loader = this.querySelector('[data-locator-loader]');
    this.sentinel = this.querySelector('[data-locator-sentinel]');
    this.status = this.querySelector('[data-locator-status]');
    this.cardTemplate = this.querySelector('[data-locator-card-template]');
    this.overlay = this.querySelector('[data-locator-overlay]');
    this.mapCanvas = this.querySelector('[data-locator-map-canvas]');
    this.mapFallback = this.querySelector('[data-locator-map-fallback]');
    this.pinCard = this.querySelector('[data-locator-pincard]');
    this.pinCardClose = this.querySelector('[data-locator-pincard-close]');
    this.toggle = this.querySelector('[data-locator-toggle]');
    this.toggleLabel = this.querySelector('[data-locator-toggle-label]');
    this.toggleIconMap = this.querySelector('[data-locator-toggle-icon-map]');
    this.toggleIconList = this.querySelector('[data-locator-toggle-icon-list]');
  }

  bindListeners() {
    this.debouncedSearch = debounce(() => this.applyFilters('search'), SEARCH_DEBOUNCE_MS);

    this.handleInput = () => {
      if (this.validation && !this.validation.hidden) this.validation.hidden = true;
      this.debouncedSearch();
    };
    this.handleSubmit = (event) => {
      event.preventDefault();

      if (!this.input?.value.trim()) {
        if (this.validation) this.validation.hidden = false;
        return;
      }

      if (this.validation) this.validation.hidden = true;
      this.debouncedSearch.cancel();
      this.applyFilters('search');
    };
    this.handleChipClick = (event) => {
      const chip = event.target.closest('[data-locator-chip]');
      if (!chip) return;
      for (const other of this.chipRow.querySelectorAll('[data-locator-chip]')) {
        const isActive = other === chip;
        other.classList.toggle('salon-near-me__chip--active', isActive);
        other.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      }
      this.state.chip = chip.dataset.locatorChipValue ?? '';
      this.applyFilters('filter');
    };
    this.handleCardClick = (event) => {
      if (!window.matchMedia(DESKTOP_QUERY).matches) return;
      const card = event.target.closest('[data-locator-card]');
      if (!card) return;
      this.activateCard(card.dataset.salonId, false);
      this.dispatchEvent(
        new CustomEvent('salon:select', {
          detail: { id: card.dataset.salonId, source: 'card' },
        }),
      );
    };
    this.handleBannerClick = () => this.requestGeolocation();
    this.handleResetClick = () => this.resetChip();
    this.handleToggleClick = () => this.toggleView();
    this.handlePinSelect = (event) => {
      const salonId = String(event.detail?.id ?? '');
      this.focusSalon(salonId);
      this.mapView?.setActive(salonId, { fly: false });
      if (!window.matchMedia(DESKTOP_QUERY).matches) this.renderPinCard(salonId);
    };
    this.handleResults = (event) => {
      const { salons, reason, centre, zoom, hasOrigin } = event.detail ?? {};
      this.mapView?.setSalons(salons ?? [], {
        reason,
        centre: hasOrigin === true ? centre : null,
        zoom,
      });
      this.featureNearest();
    };
    this.handleSelect = (event) => this.mapView?.setActive(String(event.detail?.id ?? ''));
    this.handlePinCardClose = () => this.hidePinCard();
    this.handleMapReady = () => {
      if (this.mapFallback) this.mapFallback.hidden = true;
    };
    this.handleMapFailed = () => {};

    this.input?.addEventListener('input', this.handleInput);
    this.form?.addEventListener('submit', this.handleSubmit);
    this.chipRow?.addEventListener('click', this.handleChipClick);
    this.list?.addEventListener('click', this.handleCardClick);
    this.bannerButton?.addEventListener('click', this.handleBannerClick);
    this.messageReset?.addEventListener('click', this.handleResetClick);
    this.toggle?.addEventListener('click', this.handleToggleClick);
    this.pinCardClose?.addEventListener('click', this.handlePinCardClose);
    this.addEventListener('salon:results', this.handleResults);
    this.addEventListener('salon:select', this.handleSelect);
    this.addEventListener('salon:pinselect', this.handlePinSelect);
    this.addEventListener('salon:mapready', this.handleMapReady);
    this.addEventListener('salon:mapfailed', this.handleMapFailed);
  }

  unbindListeners() {
    this.input?.removeEventListener('input', this.handleInput);
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.chipRow?.removeEventListener('click', this.handleChipClick);
    this.list?.removeEventListener('click', this.handleCardClick);
    this.bannerButton?.removeEventListener('click', this.handleBannerClick);
    this.messageReset?.removeEventListener('click', this.handleResetClick);
    this.toggle?.removeEventListener('click', this.handleToggleClick);
    this.pinCardClose?.removeEventListener('click', this.handlePinCardClose);
    this.removeEventListener('salon:results', this.handleResults);
    this.removeEventListener('salon:select', this.handleSelect);
    this.removeEventListener('salon:pinselect', this.handlePinSelect);
    this.removeEventListener('salon:mapready', this.handleMapReady);
    this.removeEventListener('salon:mapfailed', this.handleMapFailed);
  }

  observeSection() {
    this.sectionObserver = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        this.sectionObserver.disconnect();
        this.load();
        if (window.matchMedia(DESKTOP_QUERY).matches) this.ensureMap();
      },
      { rootMargin: SECTION_ROOT_MARGIN },
    );
    this.sectionObserver.observe(this);
  }

  ensureMap() {
    if (!this.mapPromise) this.mapPromise = this.buildMap();
    return this.mapPromise;
  }

  async buildMap() {
    if (!this.mapCanvas) return;

    try {
      await ensureMapAssets(this.config.assets);
    } catch (error) {
      console.warn('Salon Near Me: the map assets could not load. Every salon address is still in the list.', error);
      this.dispatchEvent(new CustomEvent('salon:mapfailed', { detail: { reason: 'assets' } }));
      return;
    }

    if (!this.isConnected || this.mapView) return;

    this.mapView = new window.SalonMapView(this.mapCanvas, {
      tileUrl: this.config.tileUrl,
      tileAttr: this.config.tileAttr,
      lat: this.config.lat,
      lng: this.config.lng,
      zoom: this.config.zoom,
      onPinSelect: (id) => this.dispatchEvent(new CustomEvent('salon:pinselect', { detail: { id } })),
      onReady: () => this.dispatchEvent(new CustomEvent('salon:mapready', { detail: {} })),
      onFail: (reason) => this.dispatchEvent(new CustomEvent('salon:mapfailed', { detail: { reason } })),
    });
    this.mapView.init();
    // Results that landed before the map existed (e.g. location granted first) keep their framing.
    const { origin } = this.state;
    this.mapView.setSalons(this.state.filtered, {
      reason: 'init',
      centre: origin ? [origin.lat, origin.lng] : null,
      zoom: origin ? DISTANCE_ZOOM : this.config.zoom,
    });
    this.featureNearest();
  }

  // The pin twin of the card's "Nearest" badge: same rule, so pin and card always agree.
  featureNearest() {
    if (this.state.sortMode !== 'distance' || this.state.originSource === 'pin') return;
    const [nearest] = this.state.filtered;
    if (nearest) this.mapView?.setActive(String(nearest.id), { fly: false });
  }

  async load() {
    let salons = null;
    try {
      salons = await fetchSalons(this.config.salonUrl);
    } catch (error) {
      if (this.wrapper) this.wrapper.hidden = true;
      console.warn(
        `Salon Near Me: could not load salon data from the Locator Data URL setting (${this.config.salonUrl}).`,
        error,
      );
      return;
    }

    if (!this.isConnected) return;
    this.state.all = salons;
    this.state.ready = true;
    // ?state=Maharashtra (homepage state tiles) opens on that state; auto-location would narrow it to nearby salons.
    const stateParam = new URLSearchParams(window.location.search).get('state')?.trim() ?? '';
    if (stateParam !== '' && this.input) this.input.value = stateParam;
    else this.maybeOfferGeolocation();
    this.applyFilters('init');
  }

  async applyFilters(reason) {
    if (!this.state.ready) return;

    this.filterToken += 1;
    const token = this.filterToken;
    const rawQuery = this.input?.value ?? '';
    const digits = rawQuery.replace(/\D/g, '');
    const isPinQuery = digits.length === PIN_LENGTH;

    this.state.query = rawQuery.trim();

    if (isPinQuery) {
      const origin = await this.resolveOrigin(digits);
      if (!this.isConnected || token !== this.filterToken) return;
      this.clearFocus();
      this.state.origin = origin;
      this.state.sortMode = origin ? 'distance' : 'city';
    } else if (this.state.originSource === 'pin' || this.state.originSource === 'place') {
      // A chip filter refines the current result; anything else is a new intent, so
      // the derived origin is dropped and a geolocated one restored.
      if (reason !== 'filter') this.releaseFocus();
    } else if (reason !== 'geo' && this.state.sortMode === 'distance' && !this.pinnedByGeolocation) {
      this.state.origin = null;
      this.state.originSource = null;
      this.state.sortMode = 'city';
    }

    if (!this.state.origin) this.state.distances = new Map();

    const searchKey = isPinQuery ? '' : this.resolveKey(rawQuery);
    const matched = this.matchByChip(this.matchByText(searchKey));

    if (this.state.originSource === 'pin' && !this.isFocusIn(matched)) {
      this.releaseFocus();
    }

    this.hideMessage();
    this.state.unmatched = false;

    if (matched.length === 0 && this.state.chip !== '') {
      this.showChipEmptyMessage();
      this.state.filtered = [];
    } else if (matched.length === 0 && searchKey !== '') {
      // The named place wins over a geolocated origin: the user asked about Rajkot,
      // so "the 5 nearest" has to mean nearest to Rajkot, not to wherever they are.
      const place = await this.resolvePlace(searchKey, rawQuery);
      if (!this.isConnected || token !== this.filterToken) return;
      const origin = place ?? this.state.origin;

      if (origin) {
        if (place) {
          this.state.origin = place;
          this.state.originSource = 'place';
        }
        this.state.sortMode = 'distance';
        this.setMessage(`No partner salon in ${this.state.query} — here are the ${this.config.minResults} nearest.`);
        this.state.filtered = this.confirmedFirst(this.sortByDistance(this.matchByChip(this.state.all)));
      } else {
        this.setMessage(`We have no partner salon matching "${this.state.query}" yet.`);
        if (this.messageNote) this.messageNote.hidden = false;
        this.state.unmatched = true;
        this.state.filtered = this.matchByChip(this.state.all);
      }
    } else {
      this.state.filtered = this.state.sortMode === 'distance' ? this.confirmedFirst(this.sortByDistance(matched)) : matched;
    }

    this.computeVisible();
    this.state.rendered = 0;
    this.state.activeId = null;
    this.hidePinCard();
    this.list.replaceChildren();
    this.renderBatch();
    this.observeList();
    this.updateOverlay();

    this.dispatchEvent(
      new CustomEvent('salon:results', {
        detail: {
          salons: this.state.filtered,
          query: this.state.query,
          centre: this.state.origin
            ? [this.state.origin.lat, this.state.origin.lng]
            : [this.config.lat, this.config.lng],
          zoom: this.state.origin ? DISTANCE_ZOOM : this.config.zoom,
          hasOrigin: Boolean(this.state.origin),
          reason,
        },
      }),
    );

    if (this.state.originSource === 'pin' && this.state.focusId !== null) {
      this.activateCard(this.state.focusId, false);
      this.mapView?.setActive(this.state.focusId, { fly: false });
    }
  }

  resolveKey(rawQuery) {
    const key = matchKey(rawQuery);
    if (key === '') return '';
    const { aliases } = this.config;
    if (aliases.has(key)) return aliases.get(key);
    return key
      .split(' ')
      .map((word) => aliases.get(word) ?? word)
      .join(' ');
  }

  matchByText(searchKey) {
    if (searchKey === '') return [...this.state.all];

    // A state name lists that state's salons only; the loose tiers would also catch "Delhi Road" elsewhere.
    const inState = this.state.all.filter((salon) => matchKey(salon.state) === searchKey);
    if (inState.length > 0) return inState;

    const queryWords = searchKey.split(' ').filter((word) => word !== '');
    if (queryWords.length === 0) return [...this.state.all];

    const tiers = [[], [], [], [], [], []];
    for (const salon of this.state.all) {
      const tier = matchTier(salon, queryWords, searchKey);
      if (tier >= 0) tiers[tier].push(salon);
    }
    return tiers.flat();
  }

  // Salons with no treatments listed may still offer the chip's treatment, so they follow the confirmed ones.
  matchByChip(salons) {
    if (this.state.chip === '') return salons;
    return this.confirmedFirst(salons.filter((salon) => isUnconfirmed(salon) || salon.treatments.includes(this.state.chip)));
  }

  // Stable: keeps the incoming order (text tier or distance) within each group.
  confirmedFirst(salons) {
    if (this.state.chip === '') return salons;
    return [...salons.filter((salon) => !isUnconfirmed(salon)), ...salons.filter(isUnconfirmed)];
  }

  sortByDistance(salons) {
    const { origin } = this.state;
    if (!origin) return salons;
    this.state.distances = new Map();
    const located = [];
    const unlocated = [];
    for (const salon of salons) {
      if (typeof salon.lat === 'number' && typeof salon.lng === 'number') {
        this.state.distances.set(salon.id, haversineKm(origin.lat, origin.lng, salon.lat, salon.lng));
        located.push(salon);
      } else {
        unlocated.push(salon);
      }
    }
    located.sort((a, b) => this.state.distances.get(a.id) - this.state.distances.get(b.id));
    return [...located, ...unlocated];
  }

  // A place name the catalogue has never heard of can still be a real town, so the
  // no-match state resolves it to coordinates and answers with the nearest salons.
  async resolvePlace(searchKey, rawQuery) {
    if (this.config.placeUrl === '') return null;

    let places = null;
    try {
      places = await fetchPlaces(this.config.placeUrl);
    } catch {
      return null;
    }

    // The alias map rewrites the query to match salon records ("bengaluru" →
    // "bangalore"); the directory may only know the other spelling, so try both.
    for (const key of [searchKey, matchKey(rawQuery)]) {
      const centroid = places?.[key];
      if (Array.isArray(centroid) && centroid.length === 2) {
        return { lat: centroid[0], lng: centroid[1] };
      }
    }
    return null;
  }

  async resolveOrigin(pin) {
    const exact = this.state.all.find(
      (salon) => salon.pincode === pin && typeof salon.lat === 'number' && typeof salon.lng === 'number',
    );
    if (exact) return { lat: exact.lat, lng: exact.lng };

    if (this.config.pinUrl !== '') {
      try {
        const centroids = await fetchCentroids(this.config.pinUrl);
        const centroid = centroids?.[pin];
        if (Array.isArray(centroid) && centroid.length === 2) {
          return { lat: centroid[0], lng: centroid[1] };
        }
      } catch {
        // A failed or missing centroid file falls through to the prefix average below.
      }
    }

    const prefix = pin.slice(0, PIN_PREFIX_LENGTH);
    const nearby = this.state.all.filter(
      (salon) =>
        typeof salon.lat === 'number' &&
        typeof salon.lng === 'number' &&
        String(salon.pincode ?? '').startsWith(prefix),
    );
    if (nearby.length === 0) return null;
    const totals = nearby.reduce(
      (accumulator, salon) => ({
        lat: accumulator.lat + salon.lat,
        lng: accumulator.lng + salon.lng,
      }),
      { lat: 0, lng: 0 },
    );
    return { lat: totals.lat / nearby.length, lng: totals.lng / nearby.length };
  }

  computeVisible() {
    const { filtered, origin, distances } = this.state;
    if (!origin) {
      this.state.visible = filtered;
      this.state.visibleRule = 'all';
      return;
    }

    const withinRadius = filtered.filter((salon) => {
      const distance = distances.get(salon.id);
      return typeof distance === 'number' && distance <= this.config.radiusKm;
    });

    if (withinRadius.length >= this.config.minResults) {
      this.state.visible = withinRadius;
      this.state.visibleRule = 'radius';
      return;
    }

    this.state.visible = filtered.slice(0, this.config.minResults);
    this.state.visibleRule = withinRadius.length === this.state.visible.length ? 'radius' : 'floor';
  }

  renderBatch() {
    const { visible, rendered } = this.state;
    const slice = visible.slice(rendered, rendered + this.config.batch);
    if (slice.length === 0) return;

    const fragment = document.createDocumentFragment();
    slice.forEach((salon, index) => {
      fragment.appendChild(this.buildCard(salon, rendered + index === 0));
    });
    this.list.appendChild(fragment);
    this.state.rendered += slice.length;
    this.announceProgress();
  }

  buildCard(salon, isFirst) {
    const card = this.cardTemplate.content.firstElementChild.cloneNode(true);
    this.fillCard(card, salon, {
      showNearest: isFirst && this.state.sortMode === 'distance' && this.state.originSource !== 'pin',
    });
    return card;
  }

  fillCard(card, salon, options = {}) {
    card.dataset.salonId = salon.id;

    const field = (name) => card.querySelector(`[data-field="${name}"]`);
    const place = salon.area || salon.city || '';
    field('name').textContent = place === '' ? salon.name : `${salon.name} — ${place}`;
    field('address').textContent = salon.address ?? '';

    if (typeof salon.rating === 'number') {
      field('rating').textContent = String(salon.rating);
      field('rating-wrap').hidden = false;
    }

    const distance = this.state.distances.get(salon.id);
    const isFocused = String(salon.id) === this.state.focusId;
    if (this.state.origin && typeof distance === 'number' && !isFocused) {
      const distancePill = field('distance');
      distancePill.textContent = formatKm(distance);
      distancePill.hidden = false;
    }

    if (options.showNearest === true) field('nearest').hidden = false;
    if (salon.bg_certified === true) field('certified').hidden = false;

    const treatments = salon.treatments ?? [];
    if (this.state.chip !== '' && isUnconfirmed(salon)) field('unconfirmed').hidden = false;
    const tagsWrapper = field('tags');
    const tagPrototype = field('tag-template');
    if (treatments.length > 0) {
      for (const treatment of treatments) {
        const tag = tagPrototype.cloneNode(true);
        tag.removeAttribute('data-field');
        tag.hidden = false;
        tag.textContent = treatment;
        tagsWrapper.appendChild(tag);
      }
      tagsWrapper.hidden = false;
    }
    tagPrototype.remove();

    if (salon.phone) {
      const call = field('call');
      call.href = `tel:${salon.phone}`;
      call.hidden = false;
    }

    if (typeof salon.lat === 'number' && typeof salon.lng === 'number') {
      const directions = field('directions');
      directions.href = `https://www.google.com/maps/dir/?api=1&destination=${salon.lat},${salon.lng}`;
      directions.hidden = false;
    }

    return card;
  }

  observeList() {
    this.listObserver?.disconnect();
    if (!this.sentinel || this.state.rendered >= this.state.visible.length) return;

    this.listObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) this.loadNextBatch();
      },
      { root: this.panel, rootMargin: LIST_ROOT_MARGIN },
    );
    this.listObserver.observe(this.sentinel);
  }

  loadNextBatch() {
    if (this.isLoading || this.state.rendered >= this.state.visible.length) return;
    this.isLoading = true;
    if (this.loader) this.loader.hidden = false;
    this.renderBatch();
    if (this.loader) this.loader.hidden = true;
    this.isLoading = false;
    if (this.state.rendered >= this.state.visible.length) this.listObserver?.disconnect();
  }

  announceProgress() {
    if (!this.status) return;
    const { rendered, visible, visibleRule, originSource, focusName } = this.state;
    const count = visible.length;
    const radius = this.config.radiusKm;
    let target = '';
    if (originSource === 'pin' && Boolean(focusName)) target = focusName;
    else if (this.state.origin && this.state.query !== '') target = this.state.query;
    else if (this.state.origin) target = 'your location';

    if (visibleRule === 'radius' && target !== '') {
      this.status.textContent = `Showing ${rendered} of ${count} salons within ${radius} km of ${target}.`;
    } else if (visibleRule === 'floor') {
      const suffix = target === '' ? '' : ` to ${target}`;
      this.status.textContent = `Showing the ${count} nearest salons${suffix}. None within ${radius} km.`;
    } else {
      this.status.textContent = `Showing ${rendered} of ${count} salons.`;
    }
  }

  updateOverlay() {
    if (!this.overlay) return;
    const count = this.state.visible.length;
    const noun = count === 1 ? 'salon' : 'salons';
    let scope = '';
    if (this.state.originSource === 'pin') scope = this.state.focusName ?? '';
    else if (this.state.query !== '') scope = this.state.query;
    else if (this.state.origin) scope = 'Near you';

    if (count === 0) {
      this.overlay.hidden = true;
      return;
    }

    // The result set is the whole catalogue, not an answer to the query — labelling
    // it with the query would contradict the "no partner salon" message beside it.
    if (this.state.unmatched || scope === '') {
      this.overlay.textContent = `${count} salons across India`;
    } else if (this.state.visibleRule === 'radius') {
      this.overlay.textContent = `${scope} · ${count} ${noun} within ${this.config.radiusKm} km`;
    } else if (this.state.visibleRule === 'floor') {
      this.overlay.textContent = `${scope} · nearest ${count} ${noun}`;
    } else {
      this.overlay.textContent = `${scope} · ${count} ${noun} found`;
    }
    this.overlay.hidden = false;
  }

  setMessage(text) {
    if (!this.message) return;
    this.messageText.textContent = text;
    this.message.hidden = false;
  }

  hideMessage() {
    if (!this.message) return;
    this.message.hidden = true;
    this.messageText.textContent = '';
    if (this.messageNote) this.messageNote.hidden = true;
    if (this.messageReset) this.messageReset.hidden = true;
  }

  showChipEmptyMessage() {
    const activeChip = this.chipRow?.querySelector('.salon-near-me__chip--active');
    const chipLabel = activeChip?.textContent.trim() ?? this.state.chip;
    const scope = this.state.query === '' ? 'in this area' : `in ${this.state.query}`;
    this.setMessage(`No salons offer ${chipLabel} ${scope}.`);

    const defaultChip = this.defaultChip();
    if (!defaultChip || !this.messageReset) return;
    this.messageReset.textContent = defaultChip.textContent.trim();
    this.messageReset.hidden = false;
  }

  defaultChip() {
    const chips = this.chipRow?.querySelectorAll('[data-locator-chip]') ?? [];
    for (const chip of chips) {
      if ((chip.dataset.locatorChipValue ?? '') === '') return chip;
    }
    return null;
  }

  resetChip() {
    const defaultChip = this.defaultChip();
    if (!defaultChip) return;
    defaultChip.click();
  }

  focusSalon(salonId) {
    const salon = this.state.filtered.find((record) => String(record.id) === salonId);
    if (!salon || typeof salon.lat !== 'number' || typeof salon.lng !== 'number') {
      this.activateCard(salonId, true);
      return;
    }

    this.state.origin = { lat: salon.lat, lng: salon.lng };
    this.state.originSource = 'pin';
    this.state.focusId = salonId;
    this.state.focusName = salon.name ?? '';
    this.state.sortMode = 'distance';
    this.state.filtered = this.sortByDistance(this.state.filtered);
    this.computeVisible();

    this.state.rendered = 0;
    this.list.replaceChildren();
    this.renderBatch();
    this.observeList();
    this.panel?.scrollTo({ top: 0 });
    this.activateCard(salonId, false);
    this.updateOverlay();
  }

  isFocusIn(salons) {
    return salons.some((record) => String(record.id) === this.state.focusId);
  }

  clearFocus() {
    this.state.originSource = null;
    this.state.focusId = null;
    this.state.focusName = null;
  }

  releaseFocus() {
    this.clearFocus();
    this.state.origin = this.geoOrigin;
    this.state.sortMode = this.geoOrigin ? 'distance' : 'city';
  }

  activateCard(salonId, shouldScroll) {
    if (salonId === '') return;
    this.state.activeId = salonId;
    for (const card of this.list.querySelectorAll('[data-locator-card]')) {
      const isActive = card.dataset.salonId === salonId;
      card.classList.toggle('salon-near-me__card--active', isActive);
      if (isActive && shouldScroll) card.scrollIntoView({ block: 'nearest' });
    }
  }

  renderPinCard(salonId) {
    if (!this.pinCard || salonId === '') return;
    const salon = this.state.filtered.find((record) => String(record.id) === salonId);
    if (!salon) return;

    this.emptyPinCard();
    const card = this.cardTemplate.content.firstElementChild.cloneNode(true);
    this.fillCard(card, salon, { showNearest: false });
    this.pinCard.insertBefore(card, this.pinCardClose);
    this.pinCard.hidden = false;
  }

  closePinCard() {
    if (!this.pinCard || this.pinCard.hidden) return;
    this.emptyPinCard();
    this.pinCard.hidden = true;
  }

  hidePinCard() {
    this.closePinCard();
    this.mapView?.setActive(null);
    this.state.activeId = null;
    for (const card of this.list.querySelectorAll('[data-locator-card]')) {
      card.classList.remove('salon-near-me__card--active');
    }
  }

  emptyPinCard() {
    for (const node of [...this.pinCard.children]) {
      if (node !== this.pinCardClose) node.remove();
    }
  }

  maybeOfferGeolocation() {
    if (!this.banner || !navigator.geolocation || this.state.origin) return;

    let permissionQuery = null;
    try {
      permissionQuery = navigator.permissions?.query({ name: 'geolocation' }) ?? null;
    } catch {
      permissionQuery = null;
    }

    if (!permissionQuery) {
      this.banner.hidden = false;
      return;
    }

    permissionQuery
      .then((permission) => {
        if (!this.isConnected) return;
        if (permission.state === 'granted') this.requestGeolocation();
        else this.banner.hidden = false;
      })
      .catch(() => {
        if (this.isConnected) this.banner.hidden = false;
      });
  }

  requestGeolocation() {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        if (!this.isConnected) return;
        this.geoOrigin = { lat: coords.latitude, lng: coords.longitude };
        this.clearFocus();
        this.state.origin = this.geoOrigin;
        this.state.sortMode = 'distance';
        this.pinnedByGeolocation = true;
        if (this.banner) this.banner.hidden = true;
        this.applyFilters('geo');
      },
      () => {
        if (this.banner) this.banner.hidden = true;
      },
      GEO_OPTIONS,
    );
  }

  toggleView() {
    const isMap = this.dataset.locatorView !== 'map';
    this.dataset.locatorView = isMap ? 'map' : 'list';
    this.state.view = this.dataset.locatorView;
    this.toggle.setAttribute('aria-pressed', isMap ? 'true' : 'false');
    if (this.toggleLabel) this.toggleLabel.textContent = isMap ? TOGGLE_LABEL_LIST : TOGGLE_LABEL_MAP;
    if (this.toggleIconMap) this.toggleIconMap.hidden = isMap;
    if (this.toggleIconList) this.toggleIconList.hidden = !isMap;
    if (!isMap) this.closePinCard();
    if (isMap) {
      this.state.mapRequested = true;
      this.ensureMap().then(() => this.mapView?.refresh());
    }
  }
}

if (!customElements.get('salon-locator')) {
  customElements.define('salon-locator', SalonLocator);
}
